import React, { useMemo, useState } from 'react'

import { Search, TableSortType } from 'src/components/molecules'
import { ExternalAssignUserTableBody } from 'src/components/molecules/projects/ExternalAssignUserTableBody'
import {
  ExternalAssignUserSortItemType,
  ExternalAssignUserTableHead,
} from 'src/components/molecules/projects/ExternalAssignUserTableHead'
import { InternalAssignUserTableBody } from 'src/components/molecules/projects/InternalAssignUserTableBody'
import {
  InternalAssignUserSortItemType,
  InternalAssignUserTableHead,
} from 'src/components/molecules/projects/InternalAssignUserTableHead'
import { NoAssignedUserNotice } from 'src/components/molecules/projects/NoAssignedUserNotice'
import { ProjectAssignSegmentedControls } from 'src/components/molecules/projects/ProjectAssignSegmentedControls'

import { useGetExternalUsers, useGetUsers } from 'src/hooks'
import { useGetCompassRoles } from 'src/hooks/useGetCompassRoles'
import { ProfileEntity, ProjectDetailEntity } from 'src/types'

type Props = {
  project: ProjectDetailEntity
  profile?: ProfileEntity
  onClickToEditAssign: (type: 'internal' | 'external') => void
  className?: string
}

export function AssignTableWithSegmentedControls({
  project,
  profile,
  onClickToEditAssign,
  className,
}: Props) {
  const [selectedTab, setSelectedTab] = useState<'internal' | 'external'>('internal')

  const [internalSortItem, setInternalSortItem] =
    useState<InternalAssignUserSortItemType>('assignedAt')
  const [internalSortType, setInternalSortType] = useState<TableSortType>('desc')

  const [externalSortItem, setExternalSortItem] =
    useState<ExternalAssignUserSortItemType>('assignedAt')
  const [externalSortType, setExternalSortType] = useState<TableSortType>('desc')

  const [searchWord, setSearchWord] = useState<string | undefined>()

  const { compassRoles } = useGetCompassRoles()
  const { users: internalUsers } = useGetUsers()
  const { externalUsers } = useGetExternalUsers()

  const filteredInternalUsers = useMemo(() => {
    const internalAssignedUsers = project.internalUsers

    if (!searchWord) {
      return internalAssignedUsers
    }

    return internalAssignedUsers.filter(user => {
      const userKeywords = internalUsers.find(internalUser => internalUser.id === user.id)?.keywords
      const hasKeywords = userKeywords?.some(keyword => keyword.includes(searchWord)) ?? false
      const userCustomRoles = internalUsers.find(
        internalUser => internalUser.id === user.id
      )?.customRoles
      const hasCustomRoles =
        userCustomRoles?.some(customRole => customRole.name.includes(searchWord)) ?? false

      return (
        user.name?.includes(searchWord) ||
        user.email?.includes(searchWord) ||
        hasKeywords ||
        hasCustomRoles
      )
    })
  }, [internalUsers, project.internalUsers, searchWord])

  const filteredExternalUsers = useMemo(() => {
    const externalAssignedUsers = project.externalUsers

    if (!searchWord) {
      return externalAssignedUsers
    }

    return externalAssignedUsers.filter(user => {
      const userKeywords = externalUsers.find(externalUser => externalUser.id === user.id)?.keywords
      const hasKeywords = userKeywords?.some(keyword => keyword.includes(searchWord)) ?? false

      return user.name?.includes(searchWord) || user.email?.includes(searchWord) || hasKeywords
    })
  }, [externalUsers, project.externalUsers, searchWord])

  return (
    <div className={className}>
      <div className="tw-sticky tw-top-0 tw-grid tw-gap-s1 tw-bg-surface-primary">
        <div className="tw-flex tw-gap-[1.71rem] tw-items-center">
          <ProjectAssignSegmentedControls
            internalUserCompassRoleIds={filteredInternalUsers.map(user => user.compassRole.id)}
            externalUserCompassRoleIds={filteredExternalUsers.map(user => user.compassRole.id)}
            compassRoles={compassRoles}
            selectedId={selectedTab}
            onSelect={setSelectedTab}
            className="tw-w-full"
          />
          <Search
            onChange={setSearchWord}
            size="s"
            placeholder="検索"
            className="tw-w-[20rem] tw-ml-auto !tw-rounded-[0.43rem] !tw-h-[2.57rem]"
          />
        </div>

        {selectedTab === 'internal'
          ? filteredInternalUsers.length > 0 && (
              <InternalAssignUserTableHead
                sortItem={internalSortItem}
                sortType={internalSortType}
                onSelectItem={setInternalSortItem}
                onSelectSortType={setInternalSortType}
              />
            )
          : filteredExternalUsers.length > 0 && (
              <ExternalAssignUserTableHead
                sortItem={externalSortItem}
                sortType={externalSortType}
                onSelectItem={setExternalSortItem}
                onSelectSortType={setExternalSortType}
              />
            )}
      </div>

      <div>
        {selectedTab === 'internal' ? (
          filteredInternalUsers.length > 0 ? (
            <InternalAssignUserTableBody
              internalAssignedUsers={filteredInternalUsers}
              profile={profile}
              sortItem={internalSortItem}
              sortType={internalSortType}
              className="tw-border-b tw-border-x tw-border-border-secondary tw-rounded-b-[8px] tw-overflow-hidden"
            />
          ) : (
            <NoAssignedUserNotice
              onClickAddUser={() => onClickToEditAssign('internal')}
              className="tw-py-s2"
            />
          )
        ) : filteredExternalUsers.length > 0 ? (
          <ExternalAssignUserTableBody
            externalAssignedUsers={filteredExternalUsers}
            sortItem={externalSortItem}
            sortType={externalSortType}
            className="tw-border-b tw-border-x tw-border-border-secondary tw-rounded-b-[8px] tw-overflow-hidden"
          />
        ) : (
          <NoAssignedUserNotice
            onClickAddUser={() => onClickToEditAssign('external')}
            className="tw-py-s2"
          />
        )}
      </div>
    </div>
  )
}
